import { cmContractTotalAmount, cmDeliverablePaymentStatus, fmtCmSvcMoney, isCmBankGuaranteeType } from '@lib/cmServices';
import {
  isProcGoodsType,
  isProcServicesType,
  packageActivityHistory,
  resolveCmLinkForProc,
} from '@lib/procurement';
import ProcActivityHistorySection from '@feature/procurement/components/proc-activity-history';
import type { CmDeliverable } from '@feature/contract-mgmt/types/contract-mgmt-components';
import type { ProcRow } from '@type/store';
import type React from 'react';

function val(v: unknown) {
  if (v == null || v === '') return '—';
  return String(v);
}

function numVal(v: unknown) {
  const n = Number(v);
  return n ? n.toLocaleString() : '—';
}

function ReadField({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="fg2">
      <label>{label}</label>
      <input type="text" readOnly className="proc-detail-readonly" value={val(value)} />
    </div>
  );
}

function ReadTextArea({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="fg2 proc-detail-span2">
      <label>{label}</label>
      <textarea readOnly className="proc-detail-readonly" rows={2} value={val(value)} />
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="cm-form-sec-title proc-detail-span2">{children}</div>;
}

export default function ProcPackageDetailView({
  procRow,
  contractMgmt,
}: {
  procRow: ProcRow;
  contractMgmt: Record<string, ProcRow[]>;
}) {
  const cmLink = resolveCmLinkForProc(contractMgmt, procRow);
  const cm = cmLink?.row;
  const isGoods = isProcGoodsType(procRow.type);
  const isServices = isProcServicesType(procRow.type);
  const deliverables = (cm?.deliverables as CmDeliverable[] | undefined) || [];

  return (
    <div className="proc-detail-view">
      <SectionTitle>In Process / Procurement Details</SectionTitle>
      <ReadField label="Type" value={procRow.type} />
      <ReadField label="Package Reference" value={procRow.ref} />
      <ReadField label="Package Name" value={procRow.name} />
      <ReadField label="Financial Year" value={procRow.fy} />
      <ReadField label="Party / Contractor" value={procRow.party} />
      <ReadField label="Budget Amount (PKR)" value={numVal(procRow.pkr)} />
      <ReadField label="Budget Amount (USD)" value={procRow.usd} />
      <ReadField label="Advertised On Date" value={procRow.advertisedOn} />
      <ReadField label="PP Reference" value={procRow.ppRef} />
      <ReadField label="Proposal / Bid Open Date" value={procRow.bidOpenDate} />
      <ReadField label="Bid Validity Days" value={procRow.bidValidityDays} />
      <ReadField label="Bid Validity Till" value={procRow.bidValidityTill} />
      <ReadTextArea label="Firms Participated" value={procRow.firmsParticipated} />
      <ReadTextArea label="Officially Shortlisted Group" value={procRow.shortlistedGroup} />
      <ReadTextArea label="Technically Qualified Firms" value={procRow.technicallyQualified} />
      <ReadField label="Bid by Qualified Firm (Value)" value={procRow.bidByQualifiedFirm} />
      <ReadField label="Qualified Firm Name" value={procRow.qualifiedFirmName} />
      <ReadField label="Contract Sign Date" value={procRow.contractSignDate} />
      <ReadField label="Status" value={procRow.status || procRow.currentStatus} />
      <ReadTextArea label="Remarks" value={procRow.procRemarks || procRow.remarks} />

      {cm && isGoods && (
        <>
          <SectionTitle>Contract Management — Goods</SectionTitle>
          <ReadField label="Vendor" value={cm.vendor} />
          <ReadField label="Amount" value={cm.amt} />
          <ReadField label="Contract Date" value={cm.contract} />
          <ReadField label="PO Date" value={cm.po} />
          <ReadTextArea label="Delivery Schedule" value={cm.deliverySchedule} />
          <ReadField label="Delivery Status" value={cm.delivery} />
          <ReadField label="Inspection Status" value={cm.inspection} />
          <ReadField label="Inspection Remarks" value={cm.inspectionRemarks} />
          <ReadField label="Payment Status" value={cm.payment} />
          <ReadField label="Warranty Remarks" value={cm.warranty} />
          <ReadField label="Performance Security Type" value={cm.perfSecurityType} />
          <ReadField label="Performance Security Status" value={cm.perfSecurity} />
          {isCmBankGuaranteeType(cm.perfSecurityType) && (
            <ReadTextArea label="Remarks" value={cm.perfSecurityRemarks} />
          )}
          <ReadField label="Contract Status" value={cm.status} />
          <ReadTextArea label="Contract Remarks" value={cm.remarks} />
        </>
      )}

      {cm && isServices && (
        <>
          <SectionTitle>Contract Management — {procRow.type}</SectionTitle>
          <ReadField label="Firm / Consultant" value={cm.firm} />
          <ReadField label="Amount (PKR)" value={cm.pkr} />
          <ReadField label="Amount (USD)" value={cm.usd} />
          <ReadField label="Date of Contract Signing" value={cm.contract} />
          <ReadField label="Date of Commencement" value={cm.comm} />
          <div className="proc-detail-span2 cm-payment-summary cm-payment-summary--readonly">
            <div className="cm-form-sec-title">Payment Summary</div>
            <div className="cm-payment-summary-grid">
              <div className="cm-payment-summary-item">
                <span className="cm-payment-summary-label">Total Contract Amount</span>
                <strong className="cm-payment-summary-value">{fmtCmSvcMoney(cmContractTotalAmount(cm))}</strong>
              </div>
              <div className="cm-payment-summary-item">
                <span className="cm-payment-summary-label">Total Amount Paid</span>
                <strong className="cm-payment-summary-value">{fmtCmSvcMoney(cm.paidAmount)}</strong>
              </div>
              <div className="cm-payment-summary-item">
                <span className="cm-payment-summary-label">Amount Paid (Remuneration)</span>
                <strong className="cm-payment-summary-value">{fmtCmSvcMoney(cm.paidRemuneration)}</strong>
              </div>
              <div className="cm-payment-summary-item">
                <span className="cm-payment-summary-label">Amount Paid (Reimbursable)</span>
                <strong className="cm-payment-summary-value">{fmtCmSvcMoney(cm.paidReimbursable)}</strong>
              </div>
            </div>
          </div>
          <ReadField label="Total Remuneration (PKR)" value={fmtCmSvcMoney(cm.totalRemuneration)} />
          <ReadField label="Total Reimbursable (PKR)" value={fmtCmSvcMoney(cm.totalReimbursable)} />
          <ReadField label="Payment % (Manual)" value={cm.paymentPct} />
          <ReadTextArea label="Payment % Remarks" value={cm.paymentPctRemarks} />
          <ReadField label="Contract Payment Summary" value={cm.payment} />
          <ReadField label="Insurance Status" value={cm.insurance} />
          <ReadField label="Insurance Type" value={cm.insuranceType} />
          <ReadField label="Insurance Name" value={cm.insuranceName} />
          <ReadField label="Date of Issuance" value={cm.insuranceIssuance} />
          <ReadField label="Date of Expiry" value={cm.insuranceExpiry} />
          <ReadTextArea label="Insurance Remarks" value={cm.insuranceRemarks} />
          <ReadField label="Contract Status" value={cm.status} />
          <ReadTextArea label="Contract Remarks" value={cm.remarks} />

          {deliverables.length > 0 && (
            <div className="proc-detail-span2 proc-detail-deliverables">
              <div className="cm-form-sec-title">Deliverables</div>
              <div className="tbl-wrap civ-scroll">
                <table className="cm-deliverables-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Timeline</th>
                      <th>Submission</th>
                      <th>Approval</th>
                      <th>Amount</th>
                      <th>Provisional Sum</th>
                      <th>Contingency</th>
                      <th>Payment Status</th>
                      <th>Deliverable Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliverables.map((d, i) => (
                      <tr key={d.sr ?? i}>
                        <td>{d.sr}</td>
                        <td className="t-wrap">{val(d.name)}</td>
                        <td>{val(d.timeline)}</td>
                        <td>{val(d.submissionDate || d.actualSubmission)}</td>
                        <td>{val(d.approvalDate)}</td>
                        <td>{fmtCmSvcMoney(d.amount)}</td>
                        <td>{fmtCmSvcMoney(d.provisionalSum)}</td>
                        <td>{fmtCmSvcMoney(d.contingencyAmount)}</td>
                        <td>{val(cmDeliverablePaymentStatus(d))}</td>
                        <td>{val(d.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {!cm && (isGoods || isServices) && (
        <div className="proc-detail-span2 proc-detail-note">
          No linked Contract Management record found for this package.
        </div>
      )}

      <ProcActivityHistorySection history={packageActivityHistory(procRow)} />
    </div>
  );
}
